import { useState } from 'react';
import { AlertCircle, Check, Upload } from '../../icons';
import { importOrgCsv, type OrgImportResult } from '../../api/hr-org-import';
import { AdminBulkImportUserSection } from './AdminBulkImportUserSection';
// 채택 3단계 — 단추를 정본 Button 으로(D1).
import { Button } from '../../components/shared';
import './OrgBulkImport.css';

/**
 * **일괄등록** — 통합설정 › 조직 (2026-08-30 · 판 B3).
 *
 * ## 두 가지를 한 자리에 세운다
 *
 * ★★ **조직 일괄등록이 없었다.** 사용자 일괄등록은 있는데 부서·팀은 하나씩 손으로 만들어야
 *    했다 — 서른 개짜리 조직이면 서른 번이다.
 *
 * ★★ **사용자 일괄등록은 만들어 놓고 아무 데도 안 붙어 있었다**(2026-08-30에 발견). 화면
 *    컴포넌트와 번역까지 다 있는데 어느 자리에도 mount 되지 않아, **쓸 수 있는 길이 없었다.**
 *    여기서 함께 세운다 — 회사를 처음 세울 때 조직과 사람은 잇달아 하는 일이라 자리도 붙는다.
 *
 * ## 두 번 올려도 같은 결과다
 *
 * ★ 이름으로 찾아 **없으면 만들고 있으면 둔다**. 일괄등록은 실패하면 다시 올리게 되는데,
 *   그때 같은 이름의 부서가 둘로 늘면 조직도가 통째로 무너진다.
 */

const SAMPLE = 'division,team,leader\n경영지원본부,인사팀,hr@example.com\n경영지원본부,총무팀,\n개발본부,,\n';

export function OrgBulkImportSection(): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<OrgImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await importOrgCsv(file));
    } catch (e) {
      const d = (e as { response?: { data?: { message?: string } } })?.response?.data;
      // 서버가 «왜 안 되는지»를 사람 말로 준다 — 그대로 보여 준다(다시 쓰면 둘이 갈라진다).
      setError(d?.message ?? '올리지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const downloadSample = () => {
    // 엑셀이 한글을 안 깨뜨리게 BOM 을 앞에 둔다.
    const blob = new Blob([`﻿${SAMPLE}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '조직등록_양식.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="obi">
      <section aria-label="조직 일괄등록">
        <h2 className="obi__title">조직 일괄등록</h2>
        <p className="obi__sub">
          부서와 팀을 CSV 로 한 번에 만듭니다. <b>같은 이름이 이미 있으면 그대로 둡니다</b> —
          두 번 올려도 조직이 둘로 늘지 않습니다.
        </p>

        <table className="obi__spec">
          <thead>
            <tr><th scope="col">칸</th><th scope="col">뜻</th></tr>
          </thead>
          <tbody>
            <tr><th scope="row">division</th><td>부서 이름 <b>(필수)</b></td></tr>
            <tr><th scope="row">team</th><td>팀 이름 — 비우면 <b>부서만</b> 만듭니다</td></tr>
            <tr><th scope="row">leader</th><td>팀장의 <b>이메일</b> — 그 회사 사람이어야 합니다. 팀원이 아니면 함께 넣습니다</td></tr>
          </tbody>
        </table>

        <div className="obi__row">
          <input
            type="file" accept=".csv,text/csv" aria-label="조직 CSV 파일"
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); setResult(null); setError(null); }}
          />
          <Button variant="secondary" size="compact" onClick={downloadSample}>양식 받기</Button>
          <Button
            variant="primary"
            size="compact"
            onClick={() => { void upload(); }}
            disabled={busy || !file}
            loading={busy}
            iconLeft={busy ? undefined : <Upload size={14} aria-hidden />}
          >
            {busy ? '올리는 중…' : '올리기'}
          </Button>
        </div>

        {error && <p className="obi__err" role="alert"><AlertCircle size={14} aria-hidden />{error}</p>}

        {result && (
          <div className="obi__result" role="status">
            <p className="obi__ok">
              <Check size={14} aria-hidden />
              {result.totalRows}줄 중 — 부서 <b>{result.divisionsCreated}</b> · 팀 <b>{result.teamsCreated}</b> ·
              팀장 <b>{result.leadersSet}</b> 을 세웠습니다.
              {/* ★ 「이미 있어서 안 만든 것」을 따로 말한다 — 안 그러면 «아무 일도 안 일어났다»로 읽힌다. */}
              {result.skipped > 0 && <> 이미 있어 그대로 둔 것 {result.skipped}건.</>}
            </p>
            {result.errors.length > 0 && (
              <>
                <p className="obi__errhead">넘어간 줄 {result.errors.length}개</p>
                <ul className="obi__errs">
                  {result.errors.map((e) => (
                    <li key={`${e.row}-${e.code}`}>{e.row}줄 — {e.message}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </section>

      {/*
        ★★ 사용자 일괄등록 — **만들어 놓고 아무 데도 안 붙어 있었다**(2026-08-30 발견).
           화면도 번역도 다 있는데 mount 된 자리가 없어 쓸 길이 없었다. 조직과 사람은 회사를
           세울 때 잇달아 하는 일이라 자리도 붙여 둔다.
      */}
      <hr className="obi__sep" />
      <AdminBulkImportUserSection />
    </div>
  );
}

export default OrgBulkImportSection;
